import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Store, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "Restaurant",
  "Cafe",
  "Bar & Pub",
  "Sports Shop",
  "Retail",
  "Fitness & Gym",
  "Services",
  "Entertainment",
  "Other"
];

export default function MerchantApply() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Personal account fields
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  
  // Business fields
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [offerings, setOfferings] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [businessHours, setBusinessHours] = useState("");
  const [category, setCategory] = useState("");

  const submitApplication = async () => {
    // Validation
    if (!name || !contactEmail || !password) {
      toast.error("Please fill your name, email, and password");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (!businessName || !description || !offerings || !contactPhone || !category) {
      toast.error("Please fill all required business fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/merchant-applications/public-apply`,
        {
          // Personal info
          name,
          password,
          // Business info
          business_name: businessName,
          description,
          offerings,
          contact_email: contactEmail,
          contact_phone: contactPhone,
          address: address || null,
          website: website || null,
          business_hours: businessHours || null,
          category
        }
      );

      toast.success("Application submitted successfully!");
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-emerald-500/20 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-emerald-400" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">Application Submitted! 🎉</h1>
          
          <div className="text-left bg-[#0a0f1a] rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-lg mb-3">What happens next?</h3>
            <ul className="space-y-2 text-slate-300">
              <li className="flex items-start">
                <span className="mr-2">1️⃣</span>
                <span>Our admin team will review your application (usually within 24-48 hours)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">2️⃣</span>
                <span>You'll receive an email notification when your application is reviewed</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">3️⃣</span>
                <span>Once approved, you can login and start creating exclusive discounts for SCC members!</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-500/10 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-300">
              <strong>Your account has been created!</strong><br />
              Email: <span className="font-mono">{contactEmail}</span><br />
              You can login anytime to check your application status.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <Button
              onClick={() => navigate("/")}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              Login to Check Status
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-3xl mx-auto py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="text-purple-400 hover:text-purple-400"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Button>
        </div>

        <Card className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-purple-500/20 p-3 rounded-full">
              <Store className="w-8 h-8 text-purple-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Become a Partner Merchant</h1>
              <p className="text-slate-400">Join our network and reach 500+ active members</p>
            </div>
          </div>

          <div className="bg-purple-500/10 rounded-lg p-4 mb-8">
            <p className="text-sm text-slate-300">
              <strong>Benefits of partnership:</strong> Direct access to SCC Football's engaged community, 
              featured placement in our portal, easy discount management, and increased foot traffic to your business.
            </p>
          </div>

          {/* Personal Account Section */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b">
              1️⃣ Create Your Account
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-1">This will be your login email</p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Password <span className="text-red-500">*</span>
                </label>
                <Input
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Business Information Section */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b">
              2️⃣ Business Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Your Business Name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-slate-600 rounded-md p-2 bg-slate-800 text-white"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="" className="text-slate-400">Select a category</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat} className="text-white">{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Business Description <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Tell us about your business..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  What can you offer to SCC members? <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="e.g., 10% off all items, Free drink with meal, etc."
                  value={offerings}
                  onChange={(e) => setOfferings(e.target.value)}
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Contact Phone <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  placeholder="+65 1234 5678"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Business Address
                </label>
                <Input
                  placeholder="123 Main Street, Singapore"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Website
                </label>
                <Input
                  placeholder="https://yourbusiness.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Business Hours
                </label>
                <Input
                  placeholder="Mon-Fri: 9am-6pm, Sat-Sun: 10am-4pm"
                  value={businessHours}
                  onChange={(e) => setBusinessHours(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            onClick={submitApplication}
            disabled={submitting}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-6 text-lg"
          >
            {submitting ? "Submitting..." : "🚀 Submit Application"}
          </Button>

          <p className="text-xs text-center text-slate-400 mt-4">
            By submitting, you agree to our terms and conditions. <br />
            Already have an account? <button onClick={() => navigate("/")} className="text-purple-400 underline">Login here</button>
          </p>
        </Card>
      </div>
    </div>
  );
}
